import { createTheme } from '@mui/material/styles';

export const darkTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,      // Mobile portrait
      sm: 600,    // Mobile landscape / small tablet
      md: 900,    // Tablet
      lg: 1200,   // Desktop
      xl: 1536,   // Large desktop
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#00695C',        // Deep Teal - Main Brand
      light: '#439A97',       // Primary Light - Hover states, highlights
      dark: '#004D47',        // Primary Dark - Active states, pressed buttons
      contrastText: '#E8F4F8', // Text on primary colored elements
    },
    secondary: {
      main: '#FF6B35',        // Vibrant Orange - Energy/Action
      light: '#FF8A65',       // Secondary Light - Hover states
      dark: '#D84315',        // Secondary Dark - Active states
      contrastText: '#2E1A16', // Text on secondary elements (dark for contrast)
    },
    background: {
      default: '#0F1419',     // Background Default - Main application background
      paper: '#1A1F24',       // Background Paper - Cards, panels, dialogs
    },
    text: {
      primary: '#E8F4F8',     // Text Primary - Main text, headings
      secondary: '#CFD8DC',   // Text Secondary - Secondary information, labels (lightened for better contrast)
      disabled: '#78909C',    // Text Disabled - Disabled states, placeholders (lightened)
    },
    divider: 'rgba(255, 255, 255, 0.12)', // General dividers, separators

    // Semantic colors
    success: {
      main: '#4caf50',
      light: '#7bc67e',
      dark: '#2e7d32',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57600',
      contrastText: '#ffffff',
    },
    error: {
      main: '#f44336',
      light: '#f88078',
      dark: '#d32f2f',
      contrastText: '#ffffff',
    },
    info: {
      main: '#2196f3',
      light: '#6ec6ff',
      dark: '#0277bd',
      contrastText: '#ffffff',
    },

    // Interactive states
    action: {
      hover: 'rgba(255, 255, 255, 0.08)',
      selected: 'rgba(0, 105, 92, 0.16)',  // Using new primary color
      disabled: 'rgba(255, 255, 255, 0.26)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
      focus: 'rgba(0, 105, 92, 0.12)',     // Using new primary color
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      color: '#E8F4F8',      // Updated to new text primary
      fontSize: '2rem',
      fontWeight: 'bold',
    },
    h2: {
      color: '#E8F4F8',      // Updated to new text primary
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h3: {
      color: '#CFD8DC',      // Updated to new text secondary
      fontSize: '1.25rem',
      fontWeight: 500,
    },
    h4: {
      color: '#CFD8DC',      // Updated to new text secondary
      fontSize: '1rem',
      fontWeight: 500,
    },
    body1: {
      color: '#CFD8DC',      // Updated to new text secondary
      fontSize: '0.875rem',
    },
    caption: {
      color: '#78909C',      // Updated to new text disabled
      fontSize: '0.75rem',
    },
  },
  components: {
    // Mobile-first component overrides
    MuiButton: {
      styleOverrides: {
        root: {
          minHeight: 44, // Touch-friendly minimum height
          minWidth: 44,  // Touch-friendly minimum width
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          minWidth: 44,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },

    // Card components
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1F24',  // Updated to new background paper
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.5)',
          },
        },
      },
    },

    // Form components
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#242A30',  // Background Elevated
            '& fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.16)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(255, 255, 255, 0.23)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00695C',    // New primary color
            },
          },
          '& .MuiInputLabel-root': {
            color: '#E8F4F8',            // New text primary - brighter for better visibility
            fontSize: '1.1rem',          // Larger font size for better visibility
            '&.Mui-focused': {
              color: '#00695C',          // New primary color
            },
            '&:not(.MuiInputLabel-shrink)': {
              transform: 'translate(14px, -8px) scale(1)',  // Normal position when not shrunk, positioned well above to prevent overlap
            },
            '&.MuiInputLabel-shrink': {
              transform: 'translate(14px, -9px) scale(0.75)',  // Shrunk position (label moves up)
            },
          },
          '& .MuiFormHelperText-root': {
            color: '#CFD8DC',            // New text secondary
          },
        },
      },
    },

    // Navigation components
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1A1F24',    // Background Paper
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.4)',
        },
      },
    },

    // Tab navigation
    MuiTabs: {
      styleOverrides: {
        root: {
          backgroundColor: '#242A30',    // Background Elevated
          '& .MuiTab-root': {
            color: '#CFD8DC',            // Text Secondary
            '&.Mui-selected': {
              color: '#00695C',          // Primary Color
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#00695C',  // Primary Color
          },
        },
      },
    },

    // Table components
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#242A30',    // Background Elevated
          '& .MuiTableCell-head': {
            color: '#E8F4F8',          // Text Primary
            fontWeight: 600,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:nth-of-type(even)': {
            backgroundColor: '#242A30',  // Background Elevated
          },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
    },

    // Dialog components
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#242A30',    // Background Elevated
          boxShadow: '0 16px 32px rgba(0, 0, 0, 0.7)',
        },
      },
    },

    // Tooltip components
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#242A30',
          color: '#ffffff',
          fontSize: '0.875rem',
          fontWeight: 500,
        },
      },
    },

    // Alert components
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: 'rgba(76, 175, 80, 0.12)',
          border: '1px solid rgba(76, 175, 80, 0.4)',
          color: '#4caf50',
        },
        standardError: {
          backgroundColor: 'rgba(244, 67, 54, 0.12)',
          border: '1px solid rgba(244, 67, 54, 0.4)',
          color: '#f44336',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 152, 0, 0.12)',
          border: '1px solid rgba(255, 152, 0, 0.4)',
          color: '#ff9800',
        },
        standardInfo: {
          backgroundColor: 'rgba(33, 150, 243, 0.12)',
          border: '1px solid rgba(33, 150, 243, 0.4)',
          color: '#2196f3',
        },
      },
    },
  },
});